
import { useEffect } from "react";
import { LoadingSkeleton } from "@a2z/ui";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetchClient } from "@/lib/api/client";
import {
  defaultLocale,
  getLocaleDirection,
  isSupportedLocale,
  keys,
} from "@/lib/i18n/portal";

import { useTranslations } from "@/lib/i18n/intl";
import { Surface } from "@/shared/layout/Surface";
import { endpoints } from "@/lib/api/endpoints";
import { getSellerPath } from "@/lib/routing/portal-urls";

type SellerSessionPayload = {
  seller?: {
    storeSlug?: string | null;
  };
};

function getResolvedLocale(value: string | undefined) {
  return value && isSupportedLocale(value) ? value : defaultLocale;
}

export function RootRedirectPage() {
  const { locale: routeLocale } = useParams();
  const locale = getResolvedLocale(routeLocale);
  const navigate = useNavigate();
  const t = useTranslations();

  useEffect(() => {
    let isActive = true;

    const resolveEntryPath = async () => {
      try {
        const response = await apiFetchClient(endpoints.seller.auth.me, {
          cache: "no-store",
        });

        if (!response.ok) {
          if (isActive) {
            navigate(`/${locale}/login`, { replace: true });
          }
          return;
        }

        const payload = (await response.json()) as SellerSessionPayload;
        const authenticatedStoreSlug = payload.seller?.storeSlug?.trim();

        if (isActive) {
          navigate(
            authenticatedStoreSlug
              ? getSellerPath(locale, authenticatedStoreSlug)
              : `/${locale}/login`,
            { replace: true }
          );
        }
      } catch {
        if (isActive) {
          navigate(`/${locale}/login`, { replace: true });
        }
      }
    };

    void resolveEntryPath();

    return () => {
      isActive = false;
    };
  }, [locale, navigate]);

  return (
    <Surface
      locale={locale}
      direction={getLocaleDirection(locale)}
      title={t(keys.common.loading)}
    >
      <div role="status" aria-busy="true">
        <span className="sr-only">{t(keys.common.loading)}</span>
        <LoadingSkeleton variant="card" rows={3} />
      </div>
    </Surface>
  );
}
